const APIurl: string = import.meta.env.API_URL;

/** Thrown by handleResp on non-success responses; preserves Response API used by callers. */
export class HttpResponseError extends Error {
  readonly response: Response;

  constructor(response: Response) {
    super(`HTTP ${response.status}: ${response.url}`);
    this.name = 'HttpResponseError';
    this.response = response;
  }

  get status(): number {
    return this.response.status;
  }

  get url(): string {
    return this.response.url;
  }

  json(): Promise<unknown> {
    return this.response.json();
  }

  text(): Promise<string> {
    return this.response.text();
  }

  blob(): Promise<Blob> {
    return this.response.blob();
  }
}

function toSnakeCase(input: string): string {
  return input.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * Creates a URLSearchParams object from a given object of parameters.
 * Keys are converted from camelCase to snake_case.
 */
export function createUrlQueryParams(params: Record<string, unknown>): URLSearchParams {
  const URLparams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return; // skip null/undefined/empty
    if (Array.isArray(value)) {
      value.forEach((element) => {
        URLparams.append(toSnakeCase(key), String(element));
      });
    } else {
      URLparams.append(toSnakeCase(key), String(value));
    }
  });

  return URLparams;
}

export async function handleResp(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return null;
  } else {
    if ([200, 201, 401].includes(response.status)) {
      // the 'split(';')[0]' ensure we only get the real content type and no dumb data such as text/xml;charset=UTF-8;charset=utf-8
      const contentType = (response.headers.get('content-type') ?? '').split(';')[0].trim();
      if (
        [
          'application/json',
          'application/pdf',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain',
          'text/xml',
          'application/xml',
          'application/rss+xml'
        ].includes(contentType)
      ) {
        if (response.status === 401) {
          // Unauthorized
          // if a user try to changes his password but makes a mistake entering his own password, we receive a 401. So we should not redirect to login
          if (response.url === APIurl + 'user/password/edit') {
            throw new HttpResponseError(response);
          } else {
            // delete the token
            localStorage.removeItem('jwt-expiry-date-UTC');
            localStorage.removeItem('jwt-refresh-token');
            localStorage.removeItem('jwt');
            const payload = (await response.json()) as { detail?: string };
            const params: Record<string, string> = { redirect: window.location.hash.substring(1) };
            if (payload.detail == 'Logged on other device') params.error = 'loggedOnOtherDevice';
            // redirects to login page if not already on it
            if (document.location.hash.substring(2, 7) !== 'login')
              document.location = `#/login?${createUrlQueryParams(params)}`;
            // In case of 401 while login, we must throw something so that login page knows there was an issue instead of silently redirecting to login
            if (response.url === APIurl + 'login') throw new HttpResponseError(response);
            return null;
          }
        } else {
          if (['application/json'].includes(contentType)) {
            return response.json();
          } else if (
            [
              'application/pdf',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ].includes(contentType)
          ) {
            return response.blob();
          } else if (['text/plain', 'text/xml', 'application/xml', 'application/rss+xml'].includes(contentType)) {
            return response.text();
          }
        }
      } else {
        throw new HttpResponseError(response);
      }
    } else {
      throw new HttpResponseError(response);
    }
  }
}

let tokenRefreshUnderway = false;

export async function refreshJWT(): Promise<void> {
  // If the token is about to expire in less than 10 minutes (or is already expired), we try to refresh it
  if (
    localStorage.getItem('jwt-refresh-token') &&
    new Date(localStorage.getItem('jwt-expiry-date-UTC')!) < new Date(new Date().getTime() + 60 * 10000)
  ) {
    if (tokenRefreshUnderway) {
      // Wait for the token to be refreshed but not more than 10 seconds
      const startTime = Date.now();
      while (tokenRefreshUnderway && Date.now() - startTime < 10000) {
        await new Promise((r) => setTimeout(r, 100));
      }
    } else {
      try {
        tokenRefreshUnderway = true;
        // When modifying the bellow, make sure that special characters such as + and & are well sent and processed
        const postObject = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', authorization: 'Bearer ' + localStorage.getItem('jwt') },
          body: JSON.stringify({ refresh_token: localStorage.getItem('jwt-refresh-token') })
        };
        const response = (await handleResp(await fetch(APIurl + 'refresh-token', postObject))) as {
          access_token: string;
          expires_at: string;
          refresh_token: string;
        };
        localStorage.setItem('jwt', response.access_token);
        localStorage.setItem('jwt-expiry-date-UTC', response.expires_at);
        localStorage.setItem('jwt-refresh-token', response.refresh_token);
        console.info('Token refreshed by requests');
      } catch (err) {
        console.error(err);
      } finally {
        tokenRefreshUnderway = false;
      }
    }
  }
}

/**
 * @param path the API path to call (eg 'declarations' for 'api-dev.customsbridge.com/declarations')
 */
export async function myFetch(path: string, options: RequestInit = {}): Promise<Response> {
  if (localStorage.getItem('jwt')) {
    await refreshJWT();
    if (!options.headers) options.headers = {};
    (options.headers as Record<string, string>).authorization = 'Bearer ' + localStorage.getItem('jwt');
  }
  return fetch(APIurl + path, options);
}

async function makeRequest(
  method: string,
  path: string,
  body: unknown = null,
  jsonBody = true,
  headers: Record<string, string> | null = null
): Promise<unknown> {
  const options: RequestInit = {
    method,
    body: jsonBody && body ? JSON.stringify(body) : (body as BodyInit | null),
    headers: {
      ...(jsonBody && body ? { 'Content-Type': 'application/json' } : {}),
      ...(headers || {})
    }
  };
  return await handleResp(await myFetch(path, options));
}

export async function doGet(
  path: string,
  params?: Record<string, unknown> | null,
  headers: Record<string, string> | null = null
): Promise<unknown> {
  return await makeRequest('GET', params ? `${path}?${createUrlQueryParams(params)}` : path, null, true, headers);
}

export async function doDelete(path: string, body?: unknown, jsonBody = true): Promise<unknown> {
  return await makeRequest('DELETE', path, body, jsonBody);
}

export async function doPatch(path: string, body?: unknown, jsonBody = true): Promise<unknown> {
  return await makeRequest('PATCH', path, body, jsonBody);
}

export async function doPut(path: string, body?: unknown, jsonBody = true): Promise<unknown> {
  return await makeRequest('PUT', path, body, jsonBody);
}

export async function doPost(path: string, body?: unknown, jsonBody = true): Promise<unknown> {
  return await makeRequest('POST', path, body, jsonBody);
}

export function connectWebSocket(): WebSocket | undefined {
  if (localStorage.getItem('jwt'))
    return new WebSocket(APIurl.replace('http', 'ws') + 'registerws/' + localStorage.getItem('jwt'));
}

export function getApiUrl(): string {
  return APIurl;
}
